<script>
  import { onDestroy, onMount } from 'svelte';
  import { currentScreen, currentUser, matchSetup } from '../store.js';
  import { clearActiveMatch } from '../../store.js';
  import { toast, confirmDialog } from '../../ui/dialogs.js';
  import {
    createCloudTournament, getCloudTournamentByCode, joinCloudTournament,
    lockAndGenerateFixtures, listenCloudTournament, fetchMyCloudTournaments,
    deleteCloudTournament, computeCloudPointsTable, computeCloudLeaderboards
  } from '../../services/tournament-cloud.js';
  import { fetchMyGlobalTeams } from '../../services/cloud-sync.js';

  const DEFAULT_TEAM_LOGO = 'data:image/svg+xml;base64,' + btoa(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#1e3a5f"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/></svg>'
  );

  let view = 'list'; // 'list' | 'create' | 'detail'
  let myTournaments = [];
  let isLoading = false;

  // Create form
  let newName = '';
  let newOvers = 10;

  // Join by code
  let joinCode = '';
  let isJoiningLookup = false;

  // Detail (live)
  let active = null;
  let unsub = null;
  let detailTab = 'table'; // 'table' | 'fixtures' | 'leaders'
  $: isOrganizer = active && $currentUser && active.organizerUid === $currentUser.uid;
  $: pointsTable = active ? computeCloudPointsTable(active) : [];
  $: leaders = active ? computeCloudLeaderboards(active) : { orangeCap: [], purpleCap: [], sixerKing: [] };
  $: playedCount = active ? (active.fixtures || []).filter(f => f.played).length : 0;

  // Join modal
  let showJoinModal = false;
  let myGlobalTeams = [];
  let joinTeamName = '';
  let joinPlayersText = '';

  onMount(async () => {
    if (!$currentUser?.uid) {
      toast('Login required for cloud tournaments.', 'error');
      currentScreen.set('home');
      return;
    }
    await loadMine();
  });
  onDestroy(() => { if (unsub) unsub(); });

  async function loadMine() {
    isLoading = true;
    myTournaments = await fetchMyCloudTournaments();
    isLoading = false;
  }

  function goBack() {
    if (view === 'detail') {
      if (unsub) { unsub(); unsub = null; }
      active = null;
      detailTab = 'table';
      view = 'list';
      loadMine();
    } else if (view === 'create') {
      view = 'list';
    } else {
      currentScreen.set('home');
    }
  }

  function openDetail(t) {
    active = t;
    view = 'detail';
    if (unsub) unsub();
    // LIVE: every phone watching this tournament sees results the moment
    // any participant's Result screen posts them.
    unsub = listenCloudTournament(t.id, latest => { active = latest; });
  }

  async function handleCreate() {
    isLoading = true;
    try {
      const t = await createCloudTournament({
        name: newName, overs: newOvers,
        rules: { maxOversPerBowler: Math.ceil(newOvers / 5), wideValue: 1, noBallValue: 1, rebowlWides: true, rebowlNoBalls: true }
      });
      toast(`🏆 Tournament created! Join code: ${t.code}`, 'success');
      newName = '';
      openDetail(t);
    } catch (error) {
      toast(error.message || 'Create failed', 'error');
    }
    isLoading = false;
  }

  async function handleJoinLookup() {
    if (!joinCode.trim()) return;
    isJoiningLookup = true;
    const t = await getCloudTournamentByCode(joinCode);
    isJoiningLookup = false;
    if (!t) { toast('No tournament found for that code.', 'error'); return; }
    joinCode = '';
    openDetail(t);
  }

  async function openJoinModal() {
    showJoinModal = true;
    joinTeamName = '';
    joinPlayersText = '';
    myGlobalTeams = await fetchMyGlobalTeams();
  }

  async function joinWithGlobalTeam(team) {
    try {
      await joinCloudTournament(active.id, {
        name: team.name,
        username: team.username,
        players: (team.roster || []).map(r => r.name)
      });
      toast(`${team.name} joined the tournament!`, 'success');
      showJoinModal = false;
    } catch (error) {
      toast(error.message || 'Join failed', 'error');
    }
  }

  async function joinWithManualTeam() {
    const players = joinPlayersText.split(/[,\n]/).map(s => s.trim()).filter(Boolean);
    try {
      await joinCloudTournament(active.id, { name: joinTeamName, username: '', players });
      toast(`${joinTeamName} joined the tournament!`, 'success');
      showJoinModal = false;
    } catch (error) {
      toast(error.message || 'Join failed', 'error');
    }
  }

  async function handleLock() {
    if (!(await confirmDialog(`Close entries and generate round-robin fixtures for ${active.teams.length} teams? Teams cannot join after this.`, { title: 'Generate fixtures' }))) return;
    try {
      await lockAndGenerateFixtures(active);
      toast('Fixtures generated — sab phones par live!', 'success');
    } catch (error) {
      toast(error.message || 'Failed', 'error');
    }
  }

  async function handleDelete() {
    if (!(await confirmDialog('Delete this cloud tournament permanently for everyone?', { title: 'Delete tournament', danger: true }))) return;
    try {
      await deleteCloudTournament(active);
      toast('Tournament deleted.', 'success');
      goBack();
    } catch (error) {
      toast(error.message || 'Failed', 'error');
    }
  }

  async function playFixture(fixture) {
    const tA = (active.teams || []).find(t => t.name === fixture.teamA);
    const tB = (active.teams || []).find(t => t.name === fixture.teamB);
    if (!tA || !tB) { toast('Team data missing.', 'error'); return; }
    if (!(await confirmDialog(`Score ${fixture.teamA} vs ${fixture.teamB}? Result will update the cloud points table for everyone.`, { title: 'Start fixture' }))) return;

    matchSetup.set({
      teamA: { name: tA.name, captain: tA.players[0], color: '#2563eb', logo: DEFAULT_TEAM_LOGO, players: tA.players },
      teamB: { name: tB.name, captain: tB.players[0], color: '#dc2626', logo: DEFAULT_TEAM_LOGO, players: tB.players },
      playersA: [...tA.players],
      playersB: [...tB.players],
      overs: active.overs,
      mode: 'full',
      lms: true,
      tossWinner: 'A',
      tossDecision: 'bat',
      rules: active.rules,
      cloudTournamentId: active.id,
      cloudFixtureId: fixture.id
    });
    await clearActiveMatch();
    if (unsub) { unsub(); unsub = null; }
    currentScreen.set('setup');
  }

  async function shareCode() {
    const text = `🏆 "${active.name}" cricket tournament join karo!\nFFPL Cricket Scorer app me Cloud Tournaments → Join Code: ${active.code}`;
    if (navigator.share) { navigator.share({ text }).catch(() => {}); }
    else {
      try { await navigator.clipboard.writeText(text); toast('Invite copied — WhatsApp par bhejo!', 'success'); }
      catch (_) { toast(`Join code: ${active.code}`, 'success'); }
    }
  }
</script>

<div class="screen" style="overflow-y:auto; padding-bottom:60px;">
  <button class="btn btn-ghost" on:click={goBack}>← Back</button>

  {#if view === 'list'}
    <div style="display:flex; justify-content:space-between; align-items:center; margin-top:10px;">
      <h1>🌐 Cloud Tournaments</h1>
      <button class="btn btn-primary" style="padding:6px 12px; font-size:13px;" on:click={() => view = 'create'}>+ Host</button>
    </div>
    <p class="muted" style="font-size:12px; margin-top:6px;">Host karo, join code share karo — points table aur NRR sabke phone par LIVE update hota hai.</p>

    <div class="card" style="margin-top:12px;">
      <div class="field-label" style="margin-bottom:6px;">Join a tournament</div>
      <div style="display:flex; gap:8px;">
        <input type="text" bind:value={joinCode} maxlength="6" placeholder="6-digit code (e.g. XK4P7M)" style="flex:1; padding:12px; background:#0f172a; border:1px solid #334155; color:white; border-radius:6px; font-weight:900; text-transform:uppercase; letter-spacing:2px;" on:keydown={(e) => e.key === 'Enter' && handleJoinLookup()} />
        <button class="btn btn-primary" style="margin:0;" on:click={handleJoinLookup} disabled={isJoiningLookup}>{isJoiningLookup ? '...' : 'Open'}</button>
      </div>
    </div>

    {#if isLoading}
      <p style="text-align:center; padding:20px;">Loading...</p>
    {:else}
      {#each myTournaments as t, i_each (t.id || t.name || i_each)}
        <div class="card" style="margin-top:10px; cursor:pointer; display:flex; justify-content:space-between; align-items:center;" on:click={() => openDetail(t)} on:keydown={(e) => e.key === 'Enter' && openDetail(t)} tabindex="0" role="button">
          <div>
            <h3 style="margin:0; color:#fbbf24;">{t.name}</h3>
            <div class="meta" style="font-size:11px; margin-top:4px;">
              {t.status === 'open' ? '🟢 Entries open' : '🏏 Live'} · {t.teams?.length || 0} teams · Code: <b>{t.code}</b>
              {t.organizerUid === $currentUser?.uid ? ' · 👑 You host' : ''}
            </div>
          </div>
          <span style="color:#64748b;">›</span>
        </div>
      {/each}
    {/if}

  {:else if view === 'create'}
    <h1 style="margin-top:10px;">Host Cloud Tournament</h1>
    <div class="card" style="margin-top:10px;">
      <div class="field">
        <label for="ct-name">Tournament Name</label>
        <input id="ct-name" type="text" bind:value={newName} maxlength="40" placeholder="e.g. FFPL Season 4" style="width:100%; padding:10px; background:#0f172a; border:1px solid #334155; color:white; border-radius:6px;" />
      </div>
      <div class="field" style="margin-top:12px;">
        <label for="ct-overs">Match Overs</label>
        <select id="ct-overs" bind:value={newOvers} style="width:100%; padding:10px; background:#0f172a; border:1px solid #334155; color:white; border-radius:6px;">
          {#each [2, 5, 6, 8, 10, 15, 20] as o, i_each (o.id || o.name || i_each)}<option value={o}>{o} overs</option>{/each}
        </select>
      </div>
      <button class="btn btn-primary btn-block" style="margin-top:16px;" on:click={handleCreate} disabled={isLoading}>
        {isLoading ? 'Creating...' : '🏆 Create & Get Join Code'}
      </button>
    </div>

  {:else if view === 'detail' && active}
    <div style="display:flex; justify-content:space-between; align-items:flex-start; margin-top:10px; gap:10px;">
      <div style="min-width:0;">
        <h1 style="margin:0;">{active.name}</h1>
        <div class="meta" style="font-size:11px; margin-top:4px;">
          {active.status === 'open' ? '🟢 Entries open' : `🏏 ${playedCount}/${active.fixtures.length} played`} · {active.overs} ov · Host: {active.organizerName}
        </div>
      </div>
      <button class="btn btn-ghost" style="padding:6px 10px; font-size:12px; flex-shrink:0;" on:click={shareCode}>Code: <b style="color:#fbbf24;">{active.code}</b> 📤</button>
    </div>

    {#if active.status === 'open'}
      <div class="card" style="margin-top:14px;">
        <h3 style="margin-top:0;">Teams ({active.teams?.length || 0})</h3>
        {#if !active.teams || active.teams.length === 0}
          <p class="muted" style="font-size:12px;">Abhi koi team nahi — join code share karo!</p>
        {/if}
        {#each active.teams || [] as t, i_each (t.id || t.name || i_each)}
          <div style="padding:8px 0; border-bottom:1px solid #1e293b; display:flex; justify-content:space-between; align-items:center;">
            <div>
              <div style="font-weight:800; font-size:14px;">{t.name} {t.username ? `(@${t.username})` : ''}</div>
              <div class="meta" style="font-size:10px;">{t.players.length} players{t.ownerUid === $currentUser?.uid ? ' · Your team' : ''}</div>
            </div>
          </div>
        {/each}
        <div style="display:flex; gap:8px; margin-top:14px; flex-wrap:wrap;">
          <button class="btn btn-primary" style="flex:1; min-width:140px;" on:click={openJoinModal}>+ JOIN WITH MY TEAM</button>
          {#if isOrganizer}
            <button class="btn btn-primary" style="flex:1; min-width:140px; background:linear-gradient(135deg,#059669,#10b981); border:none;" disabled={(active.teams?.length || 0) < 2} on:click={handleLock}>
              ▶ GENERATE FIXTURES
            </button>
          {/if}
        </div>
      </div>
    {:else}
      <!-- LIVE TOURNAMENT: tabs -->
      <div style="display:flex; border-bottom:1px solid #1e293b; margin-top:14px; background:#0f172a;">
        {#each [['table', '📊 Points Table'], ['fixtures', '🏏 Fixtures'], ['leaders', '🏆 Leaders']] as tab (tab[0])}
          <button style="flex:1; padding:10px 0; background:none; border:none; border-bottom:3px solid {detailTab === tab[0] ? '#2563eb' : 'transparent'}; color:{detailTab === tab[0] ? '#fff' : '#64748b'}; font-weight:800; font-size:12px; cursor:pointer;" on:click={() => detailTab = tab[0]}>{tab[1]}</button>
        {/each}
      </div>

      {#if detailTab === 'table'}
        <div class="card" style="margin-top:12px; padding:12px; overflow-x:auto;">
          <table style="width:100%; border-collapse:collapse; font-size:12px;">
            <thead>
              <tr style="color:#64748b; text-align:right; font-size:10px;">
                <th style="text-align:left; padding:6px 4px;">TEAM</th>
                <th style="padding:6px 4px;">P</th><th style="padding:6px 4px;">W</th>
                <th style="padding:6px 4px;">L</th><th style="padding:6px 4px;">T</th>
                <th style="padding:6px 4px;">PTS</th><th style="padding:6px 4px;">NRR</th>
              </tr>
            </thead>
            <tbody>
              {#each pointsTable as row, i (i)}
                <tr style="border-top:1px solid #1e293b; text-align:right; color:#e2e8f0; background:{i === 0 && row.pts > 0 ? 'rgba(251,191,36,0.07)' : 'transparent'};">
                  <td style="text-align:left; padding:8px 4px; font-weight:800; color:{i === 0 && row.pts > 0 ? '#fbbf24' : '#fff'};">{i + 1}. {row.name}</td>
                  <td style="padding:8px 4px;">{row.played}</td>
                  <td style="padding:8px 4px; color:#34d399;">{row.won}</td>
                  <td style="padding:8px 4px; color:#f87171;">{row.lost}</td>
                  <td style="padding:8px 4px;">{row.tied}</td>
                  <td style="padding:8px 4px; font-weight:900; color:#fbbf24;">{row.pts}</td>
                  <td style="padding:8px 4px; color:{row.nrr >= 0 ? '#34d399' : '#f87171'};">{row.nrr >= 0 ? '+' : ''}{row.nrr.toFixed(3)}</td>
                </tr>
              {/each}
            </tbody>
          </table>
          <div class="muted" style="font-size:10px; margin-top:8px;">🔴 LIVE — result aate hi sab phones par update hota hai.</div>
        </div>
      {:else if detailTab === 'fixtures'}
        {#each active.fixtures as f, i_each (f.id || f.name || i_each)}
          <div class="card" style="margin-top:8px; padding:12px; display:flex; justify-content:space-between; align-items:center; gap:8px;">
            <div style="min-width:0;">
              <div style="font-weight:800; font-size:13px;">{f.teamA} vs {f.teamB}</div>
              {#if f.played && f.result}
                <div class="meta" style="font-size:11px; margin-top:3px; color:#34d399;">
                  {f.result.winner === 'TIE' ? 'Match tied' : `${f.result.winner} won${f.result.margin ? ` by ${f.result.margin} ${f.result.marginType}` : ''}`}
                  {#if f.result.a && f.result.b} · {f.result.a.runs}/{f.result.a.wickets} vs {f.result.b.runs}/{f.result.b.wickets}{/if}
                </div>
              {:else}
                <div class="meta" style="font-size:11px; margin-top:3px;">Not played yet</div>
              {/if}
            </div>
            {#if !f.played}
              <button class="btn btn-primary" style="padding:6px 14px; font-size:12px; font-weight:800; flex-shrink:0;" on:click={() => playFixture(f)}>SCORE ▶</button>
            {:else}
              <span style="color:#34d399; font-size:18px; flex-shrink:0;">✓</span>
            {/if}
          </div>
        {/each}
      {:else}
        {#each [['🟠 Orange Cap (Most Runs)', leaders.orangeCap, p => `${p.runs} runs (${p.balls}b)`], ['🟣 Purple Cap (Most Wickets)', leaders.purpleCap, p => `${p.wickets} wkts · ${p.runsConceded} runs`], ['💥 Six Kings', leaders.sixerKing, p => `${p.sixes} sixes`]] as [title, list, fmt]}
          <div class="card" style="margin-top:10px; padding:12px;">
            <h3 style="margin:0 0 8px 0; font-size:14px;">{title}</h3>
            {#if list.length === 0}
              <p class="muted" style="font-size:11px; margin:0;">Abhi koi data nahi.</p>
            {/if}
            {#each list as p, i (i)}
              <div style="display:flex; justify-content:space-between; padding:6px 0; border-top:1px solid #1e293b; font-size:13px;">
                <span style="font-weight:{i === 0 ? 900 : 700}; color:{i === 0 ? '#fbbf24' : '#e2e8f0'};">{i + 1}. {p.name}</span>
                <span style="color:#94a3b8;">{fmt(p)}</span>
              </div>
            {/each}
          </div>
        {/each}
      {/if}
    {/if}

    {#if isOrganizer}
      <button class="btn btn-ghost btn-block" style="margin-top:18px; color:#f87171; border-color:#7f1d1d;" on:click={handleDelete}>Delete Tournament</button>
    {/if}
  {/if}
</div>

{#if showJoinModal && active}
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div on:click={() => showJoinModal = false} style="position:fixed; inset:0; background:rgba(0,0,0,0.75); display:flex; align-items:center; justify-content:center; z-index:999; padding:16px;">
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div on:click|stopPropagation style="background:#1e293b; border:1px solid #334155; border-radius:12px; padding:20px; width:100%; max-width:420px; max-height:85vh; overflow-y:auto;">
      <h3 style="color:#34d399; margin:0 0 12px 0; font-weight:900;">Join "{active.name}"</h3>

      {#if myGlobalTeams.length > 0}
        <div class="field-label" style="margin-bottom:6px;">Apni Global Team se join karo:</div>
        {#each myGlobalTeams as t, i_each (t.id || t.name || i_each)}
          <div style="display:flex; justify-content:space-between; align-items:center; padding:10px; background:#0f172a; border-radius:8px; margin-bottom:8px;">
            <div>
              <div style="font-weight:800; font-size:14px;">{t.name}</div>
              <div class="meta" style="font-size:10px;">@{t.username} · {t.roster?.length || 0} players</div>
            </div>
            <button class="btn btn-primary" style="padding:6px 14px; font-size:12px; margin:0;" on:click={() => joinWithGlobalTeam(t)}>JOIN</button>
          </div>
        {/each}
        <div style="border-top:1px dashed #334155; margin:12px 0;"></div>
      {/if}

      <div class="field-label" style="margin-bottom:6px;">Ya manual team banao:</div>
      <input type="text" bind:value={joinTeamName} maxlength="30" placeholder="Team name" style="width:100%; padding:10px; background:#0f172a; border:1px solid #334155; color:white; border-radius:6px; margin-bottom:8px;" />
      <textarea bind:value={joinPlayersText} rows="3" placeholder="Players (comma ya new line se): Aman, Ravi, Vikram..." style="width:100%; padding:10px; background:#0f172a; border:1px solid #334155; color:white; border-radius:6px; font-family:inherit;"></textarea>
      <button class="btn btn-primary btn-block" style="margin-top:10px;" on:click={joinWithManualTeam}>+ JOIN TOURNAMENT</button>
      <button class="btn btn-ghost btn-block" style="margin-top:8px;" on:click={() => showJoinModal = false}>Cancel</button>
    </div>
  </div>
{/if}
